import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationError, NavigationStart, PRIMARY_OUTLET, ResolveEnd, Router, RoutesRecognized } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Configuration } from './config/config-model';
import { IConfigService } from './config/iconfigservice';
import { CustomEventLogMode, CustomEventType } from './enums';
import { RuleEvaluation } from './models';
import { ActionContext } from './action-context';

@Injectable()
export class MonitoringService {

    private _metricQueue: { [key: string]: CustomMetric } = {};
    private _ruleExecutionLogs: RuleEvaluation[] = [];
    private _caseNumber: string;
    appInsights: ApplicationInsights;

    private navigationId: string;
    private clientRoute: string;
    private navigationActionId: string;
    private previousNavigation: { navigationId: string; clientRoute: string; navigationActionId: string };

    constructor(
        private router: Router,
        public configService: IConfigService
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.previousNavigation = {
                    navigationId: this.navigationId,
                    clientRoute: this.clientRoute,
                    navigationActionId: this.navigationActionId,
                };
                this.navigationId = uuidv4();
                this.clientRoute = undefined;
                this.navigationActionId = ActionContext.get();
            } else if (event instanceof RoutesRecognized) {
                this.clientRoute = this.getRouteTemplate(event.state.root);
            } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
                this.navigationId = this.previousNavigation?.navigationId;
                this.clientRoute = this.previousNavigation?.clientRoute;
                this.navigationActionId = this.previousNavigation?.navigationActionId;
            }
        });
    }

    getNavigationId(): string {
        return this.navigationId;
    }

    getClientRoute(): string {
        return this.clientRoute;
    }

    getActionId(): string | undefined {
        const actionId = ActionContext.get();
        return actionId === this.navigationActionId ? undefined : actionId;
    }

    // One id per call, generated at the moment it's needed so every outgoing request has a unique request id.
    generateRequestId(): string {
        return uuidv4();
    }

    setCaseNumber(caseNumber) {
        this._caseNumber = caseNumber;
    }

    setAuthenticatedUserId(userId: string): void {
        this.appInsights.setAuthenticatedUserContext(userId);
    }

    private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
        const child = snapshot.children.find(route => route.outlet === PRIMARY_OUTLET);
        return child ? this.getActivatedComponent(child) ?? snapshot.component : snapshot.component;
    }

    private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
        const paths: string[] = [];
        let current = snapshot;
        while (current) {
            const path = current.routeConfig?.path;
            // A wildcard or custom matcher may consume identifiers, so never use snapshot.url.
            if (path) paths.push(path);
            current = current.children.find(child => child.outlet === PRIMARY_OUTLET);
        }
        return paths.join('/');
    }

    private AddGlobalProperties(properties?: { [key: string]: string }): { [key: string]: string } {
        if (!properties) {
            properties = {};
        }
        if (this.clientRoute !== undefined) properties['uwp.client.route'] = this.clientRoute;
        if (this.navigationId) properties['uwp.navigation.id'] = this.navigationId;
        return properties;
    }

    // Attaches on every piece of telemetry sent by the SDK.
    private addGlobalTelemetryInitializer() {
        this.appInsights.addTelemetryInitializer(item => {
            // HTTP dependencies use the context captured in their request headers, not completion time.
            if (item.baseType !== 'RemoteDependencyData' && this.navigationId) {
                item.data = this.AddGlobalProperties(item.data);
            }
            if (this._caseNumber) {
                item.data = item.data || {};
                item.data["caseNumber"] = this._caseNumber;
            }
        });
    }

    // header promotion to appinsights context, this only applies to API dependencies
    // that have the relevant headers.
    private addCorrelationDependencyInitializer() {
        this.appInsights.addDependencyInitializer(details => {
            const headers = details.item.properties?.requestHeaders;
            if (!headers) {
                return;
            }

            details.item.properties = details.item.properties || {};

            const requestId = headers['X-Request-Id'];
            if (requestId) {
                details.item.properties['uwp.request.id'] = requestId;
            }

            const actionId = headers['X-Action-Id'];
            if (actionId) {
                details.item.properties['uwp.action.id'] = actionId;
            }
            
            const clientRoute = headers['X-Client-Route'] ?? headers['x-client-route'];
            if (clientRoute !== undefined)  { 
                details.item.properties['uwp.client.route'] = clientRoute;
            }
            
            const navigationId = headers['X-Navigation-Id'] ?? headers['x-navigation-id'];
            if (navigationId) { 
                details.item.properties['uwp.navigation.id'] = navigationId;
            }
        });
    }

    public initLogging() {
        const instrumentation = this.configService.getConfiguration();
        this.appInsights = new ApplicationInsights({ config: {
            instrumentationKey: instrumentation.appInsights.instrumentationKey,
            endpointUrl: instrumentation.appInsights.ingestionEndpoint || undefined,
            enableCorsCorrelation: true,
            correlationHeaderDomains: this.getCorrelationHeaderDomains(instrumentation),
            enableRequestHeaderTracking: true
            } });

        this.appInsights.loadAppInsights();
        this.addGlobalTelemetryInitializer();
        this.addCorrelationDependencyInitializer();
        console.log('Monitor started...');

        this.router.events.subscribe(
            e => {
                if (e instanceof ResolveEnd) {
                    const evt: ResolveEnd = e as ResolveEnd;

                    // Navigating away is an explicit end of scope for the running action
                    // - don't let it bleed onto calls made from the new page.
                    ActionContext.end();

                    const activatedComponent = this.getActivatedComponent(evt.state.root);
                    if (activatedComponent) {
                        this.logPageView(`${activatedComponent.name} ${this.getRouteTemplate(evt.state.root)}`, evt.urlAfterRedirects);
                    }
                }
            });
    }

    // Exact allow-list of our own API hostnames, so that correlation data is correctly .. correlated .. for
    // cross domain appinsights calls.
    private getCorrelationHeaderDomains(config: Configuration): string[] {
        const urls = [config.apiUrl, config.publicApiUrl, config.sdkApiUrl, config.mrasApiUrl, config.underwritingApiUrl];

        const hostnames = urls
            .filter(url => !!url)
            .map(url => {
                try {
                    return new URL(url).hostname;
                } catch {
                    return undefined;
                }
            })
            .filter((hostname): hostname is string => !!hostname);

        return Array.from(new Set(hostnames));
    }

    public logPageView(
        name: string,
        url?: string,
        properties?: { [key: string]: string },
        measurements?: { [key: string]: number },
        duration?: number) {

        this.appInsights?.trackPageView({name: name, 
            uri: url, 
            properties: this.AddGlobalProperties(properties),
             measurements});
    }

    public logCaseEvent(name: string, caseId: string, correlationId: string, eventType: CustomEventType, mode: CustomEventLogMode) {
        const eventLog = {
            'CaseId': caseId,
            'CorrelationId': correlationId,
            'EventType': eventType,
            'Mode': `${mode}`,
            'Name': name,
            'Timestamp': moment().utc().format()
        };

        this.logEvent(name, eventLog);
    }

    public logEvent(name: string, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
        const finalProperties = this.AddGlobalProperties(properties);
        const actionId = this.getActionId();
        if (actionId) {
            finalProperties['uwp.action.id'] = actionId;
        }

        this.appInsights?.trackEvent({name: name, properties: finalProperties, measurements});
    }

    /**
     * Runs fn as a user action (see ActionContext.run) and, when this call starts the action, logs its event.
     * Joining an action that is already running logs nothing, so nested wrappers produce a single event.
     * Pass null properties to start the action without logging an event.
     */
    public runAction<T>(name: string, properties: { [key: string]: string } | null, fn: () => T): T {
        const startsAction = !ActionContext.get();

        return ActionContext.run(() => {
            if (startsAction && properties) {
                this.logEvent(name, { eventType: 'Client Runtime', ...properties });
            }

            return fn();
        });
    }

    public logError(error: Error, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
        this.appInsights?.trackException( {exception: error, properties: this.AddGlobalProperties(properties), measurements});
    }

    public beginInterviewMetric(name: string, properties?: { [key: string]: string }): MetricEvent {
        const accountCode = this.getAccountCode();
        if (properties) {             
            properties.accountCode = accountCode;
        }
        
        return this.beginMetric(name, properties);
    }

    public endInterviewMetric(metricEvent: MetricEvent) {
        this.endMetric(metricEvent);
    }

    public flushInterviewMetrics() {
        this.flushMetrics();
    }

    public beginMetric(name: string, properties?: { [key: string]: string }): MetricEvent {
        const metricKey = `${name}`;
        let metric = this._metricQueue[metricKey];

        if (!metric) {
            metric = {
                name: metricKey,
                properties,
                sum: 0,
                count: 0,
                min: 0,
                max: 0
            };

            this._metricQueue[metricKey] = metric;
        }

        const metricEvent = {
            name: metricKey,
            start: moment.utc(),
            end: moment.utc(),
        };

        return metricEvent;
    }

    public endMetric(metricEvent: MetricEvent) {

        const metric = this._metricQueue[metricEvent.name];

        if (!metric) {
            console.log(`no metric for ${name} exists to complete`);
            return;
        }

        const start = metricEvent.start;
        const end = moment.utc();
        const milliseconds = moment.duration(end.diff(start)).as('milliseconds');

        metric.count += 1;
        metric.sum += milliseconds;

        if (milliseconds > metric.max) {
            metric.max = milliseconds;
        }

        if (metric.min === 0 || milliseconds < metric.min) {
            metric.min = milliseconds;
        }
    }

    public flushMetric(key) {
        const metric = this._metricQueue[key];
        const avg = metric.sum / metric.count;

        this.appInsights.trackMetric({name: metric.name, 
            average: avg,
            max: metric.max,
            min: metric.min,
            sampleCount: metric.count,
            properties: metric.properties});            

        delete this._metricQueue[key];
    }

    public flushMetrics() {
        for (const key in this._metricQueue) {
            this.flushMetric(key);
        }
    }

    private getAccountCode() {
        let route = this.router.routerState.root;
        let accountCode: string;
        while (route) {
            accountCode = route.snapshot?.data?.account?.code ?? accountCode;
            route = route.children?.find(child => child.outlet === PRIMARY_OUTLET);
        }
        return accountCode;
    }

    public addRuleExecutionLog(ruleEvaluation: RuleEvaluation) {
        this._ruleExecutionLogs.push(ruleEvaluation);
    }

    public getRuleExecutionLogs(): RuleEvaluation[] {
        return this._ruleExecutionLogs;
    }

    public clearRuleExecutionLogs() {
        this._ruleExecutionLogs = [];
    }
}

export interface CustomEvent {
    name: string;
    properties: { [key: string]: string };
}

export interface MetricEvent {
    name: string;
    start: moment.Moment;
    end: moment.Moment;
}

export interface CustomMetric {
    name: string;
    properties: { [key: string]: string };
    count: number;
    sum: number;
    min: number;
    max: number;
}
